import { FC, useEffect } from "react";
import { Control, FieldValues } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetCountryListForDropdowns } from "../../../../api/services/client/clientApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  SearchSaveFiltersSubHeader,
} from "../../../../components";
import {
  CLIENT_TYPE_SITE_CORE_VALUES,
  MAX_LENGTH_100,
  MAX_LENGTH_50,
} from "../../../../helpers/constants";
import {
  isAffiliateClient,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { toggleToast } from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import {
  ClientSearchFormKey,
  ClientSearchFormProps,
} from "../../modelsAndSchemas/Models";
import { clientSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { ClientSearchFormWrapper } from "./ClientSearchForm.style";

interface ClientSearchProps extends FieldValues {
  control: Control<ClientSearchFormProps>;
  handleClearAllSearch: () => void;
  handleSearchClick: () => void;
  handleSaveFilter: () => void;
  disableActionBtn: boolean;
  anchorRef?: React.RefObject<HTMLButtonElement>;
  showAsModal?: boolean;
  isValidForm?: boolean;
}

const pageId = "client-search";

export const ClientSearchForm: FC<ClientSearchProps> = ({
  control,
  handleClearAllSearch,
  handleSearchClick,
  handleSaveFilter,
  handleSavedFilter,
  disableActionBtn,
  anchorRef,
  showAsModal,
  isValidForm,
}) => {
  const { isMobile } = useMediaQueries();
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const {
    fetchLabel,
    commonLabels,
    fetchComponentTitle,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const clientData = useSelector(createClientSelector);

  const { data: countryListData, isError } = useGetCountryListForDropdowns();
  const filterDropdownValue = fetchDropdownValues(
    clientSearchSiteCoreKeys.searchFormKey,
    clientSearchSiteCoreKeys.searchFilterByList,
  );

  useEffect(() => {
    if (isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isError]);

  const isClientTypeDisabled = () => {
    if (!showAsModal) {
      return false;
    }
    return showAsModal && !isAffiliateClient(clientData.clientTypeId);
  };

  const getClientTypeList = () => {
    const clientTypeList = fetchDropdownValues(
      clientSearchSiteCoreKeys.searchFormKey,
      clientSearchSiteCoreKeys.clientTypeList,
    );
    if (showAsModal && isAffiliateClient(clientData.clientTypeId)) {
      return clientTypeList.filter(
        (clientType) =>
          clientType.value !== CLIENT_TYPE_SITE_CORE_VALUES.RETAIL_CLIENT,
      );
    }

    return clientTypeList;
  };

  // Common label text generators (no wrapper)
  const renderClientFormHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={handleSavedFilter}
        onResetAllClick={handleClearAllSearch}
        isSavedFilterDisabled={disableActionBtn}
        anchorRef={anchorRef}
        resetButtonTestId={`${pageId}-clearAll`}
        savedFilterTestId={`${pageId}-savedFilter`}
      />
    );
  };

  return (
    <ClientSearchFormWrapper>
      <AccordionHeader
        showExpandCollapseIcon={!showAsModal}
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(clientSearchSiteCoreKeys.searchFormKey)}
          />
        }
        isExpanded={!isMobile}
      >
        {!showAsModal && renderClientFormHeader()}
        <form data-testid="search-form">
          <MuiGrid container className="form-container">
            <MuiGrid
              size={gridSize}
              className={gridSizeLargeClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={ClientSearchFormKey.companyName}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.companyNameLabel,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ClientSearchFormKey.companyNameFilter}
                  id={`${pageId}-${ClientSearchFormKey.companyName}`}
                  selectBoxId={`${pageId}-${ClientSearchFormKey.companyNameFilter}`}
                  maxLength={MAX_LENGTH_100}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeLargeClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={ClientSearchFormKey.clientCode}
              >
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.clientCodeLabel,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ClientSearchFormKey.clientCodeFilter}
                  id={`${pageId}-${ClientSearchFormKey.clientCode}`}
                  selectBoxId={`${pageId}-${ClientSearchFormKey.clientCodeFilter}`}
                  maxLength={MAX_LENGTH_50}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeLargeClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={ClientSearchFormKey.clientType}
              >
                <MuiSelect
                  visibleDefaultSelectOption
                  id={`${pageId}-${ClientSearchFormKey.clientType}`}
                  label={fetchLabel(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.clientType,
                  )}
                  options={getClientTypeList()}
                  placeholder={commonLabels.selectPlaceHolder}
                  disabled={isClientTypeDisabled()}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeLargeClass}
              spacing={isMobile ? 2 : 0}
            >
              <FormController
                control={control}
                name={ClientSearchFormKey.country}
              >
                <MuiSelect
                  label={fetchLabel(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.countryLabel,
                  )}
                  options={setApiDataToDropDownOption(countryListData, true)}
                  value=""
                  id={`${pageId}-${ClientSearchFormKey.country}`}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController control={control} name={ClientSearchFormKey.city}>
                <MuiSelectWithTextInput
                  options={filterDropdownValue}
                  label={fetchLabel(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.cityLabel,
                  )}
                  placeholder={commonLabels.inputPlaceHolder}
                  control={control}
                  selectBoxName={ClientSearchFormKey.cityFilter}
                  id={`${pageId}-${ClientSearchFormKey.city}`}
                  selectBoxId={`${pageId}-${ClientSearchFormKey.clientCodeFilter}`}
                  maxLength={MAX_LENGTH_50}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ClientSearchFormKey.status}
              >
                <MuiSelect
                  label={fetchLabel(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.statusLabel,
                  )}
                  options={fetchDropdownValues(
                    clientSearchSiteCoreKeys.searchFormKey,
                    clientSearchSiteCoreKeys.statusList,
                  )}
                  value=""
                  id={`${pageId}-${ClientSearchFormKey.status}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                  disabled={showAsModal}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiGrid
            spacing={isMobile ? 2 : 0}
            container
            className={`button-container ${showAsModal && "padding-horizontal"}`}
          >
            {!showAsModal && (
              <MuiButtonSecondary
                onClick={handleSaveFilter}
                id={`${pageId}-saveFilter`}
                data-testid="save-filter"
                disabled={!isValidForm}
              >
                {commonLabels.saveFilter}
              </MuiButtonSecondary>
            )}
            {showAsModal && (
              <MuiButtonSecondary
                onClick={handleClearAllSearch}
                id={`${pageId}-clearFilter`}
                data-testid="clear-filter"
              >
                {commonLabels.clearAll}
              </MuiButtonSecondary>
            )}
            <MuiButtonPrimary
              onClick={handleSearchClick}
              id={`${pageId}-search`}
              data-testid="search"
            >
              {commonLabels.search}
            </MuiButtonPrimary>
          </MuiGrid>
        </form>
      </AccordionHeader>
    </ClientSearchFormWrapper>
  );
};
